import { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';

// Ícono de "boya" pulsante — dos capas (núcleo fijo + onda que crece y se
// desvanece en loop, puro CSS) en vez de un marcador estático, para que se
// note a simple vista qué está entrando hoy (HU-010 Fase 3).
const iconoBoya = new L.DivIcon({
  className: '',
  html: '<div class="mapa-vivo-boya"><div class="mapa-vivo-boya-onda"></div><div class="mapa-vivo-boya-nucleo"></div></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const RecentrarMapa = ({ centro }) => {
  const map = useMap();
  useMemo(() => { map.setView(centro, map.getZoom()); }, [centro, map]);
  return null;
};

/**
 * Leaflet mide el tamaño de su contenedor una sola vez al montar y nunca
 * vuelve a mirarlo solo — al expandir/contraer el mapa (`.panel-mapa-vivo-
 * expandido` cambia el layout del contenedor real) el mapa seguía creyendo
 * que medía lo mismo de antes, y solo pintaba tiles para esa mitad vieja
 * (el resto quedaba gris). `invalidateSize()` es la forma oficial de
 * Leaflet de decirle "medite de nuevo" — el `setTimeout` corto es porque
 * hace falta esperar al siguiente frame para que el cambio de layout de
 * React/CSS ya se haya aplicado antes de medir.
 */
const RedimensionarAlExpandir = ({ expandido }) => {
  const map = useMap();
  useEffect(() => {
    const id = setTimeout(() => map.invalidateSize(), 80);
    return () => clearTimeout(id);
  }, [expandido, map]);
  return null;
};

/**
 * Mapa en vivo del dashboard (HU-010 Fase 3) — boyas rojas pulsantes por
 * cada incidencia de hoy, centrado en el centroide configurado en
 * Configuración (`Configuracion.mapa_lat/lng`, no un valor fijo en código).
 * `incidencias` se refresca por polling desde `AlertasDelDia.jsx` (cada 20s)
 * — acá solo se pintan los pines, no se hace ningún fetch.
 * `expandido` lo controla el padre para poder ocupar todo el ancho del
 * panel sin que este componente conozca el layout de la pantalla que lo usa.
 */
const MapaAlertasVivo = ({ centro, incidencias, expandido, onToggleExpandido }) => {
  return (
    <div className={`panel-mapa-vivo ${expandido ? 'panel-mapa-vivo-expandido' : ''}`}>
      <div className="panel-mapa-vivo-cabecera">
        <span><i className="fas fa-satellite-dish"></i> Alertas en el mapa (hoy)</span>
        <button type="button" className="panel-boton-secundario" onClick={onToggleExpandido}>
          <i className={`fas ${expandido ? 'fa-compress' : 'fa-expand'}`}></i>
          {expandido ? ' Contraer' : ' Expandir'}
        </button>
      </div>

      <div className="panel-mapa-vivo-mapa">
        <MapContainer center={centro} zoom={13} style={{ height: '100%', width: '100%' }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
          <RecentrarMapa centro={centro} />
          <RedimensionarAlExpandir expandido={expandido} />
          {incidencias.filter((i) => i.lat != null && i.lng != null).map((i) => (
            <Marker key={i.inc_inId} position={{ lat: i.lat, lng: i.lng }} icon={iconoBoya}>
              <Popup>
                <strong>{i.categoria || 'Sin categoría'}</strong>
                <br />
                {i.estado}
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
};

export default MapaAlertasVivo;
